import { createContext, useState, useContext, useEffect, FC } from 'react';
import API from '../api/api';
import { useLocation } from 'react-router';
import { EPromiseResolution, EQueryStringParam, EStaffAccountType } from '../util/constants';
import GlobalContext from './GlobalContext';

type TVerifyStaffAccountContext = {
    verifyStaffAccountUiState: EVerifyStaffAccountUiState;
    customerAccountName: string;
};

export enum EVerifyStaffAccountUiState {
    default,
    initialzed,
    ready,
    fatalError,
}

const VerifyStaffAccountContext = createContext<TVerifyStaffAccountContext | undefined>(undefined);

export const VerifyStaffAccountProvider: FC = ({ children }) => {
    const { setStaffAccountType, getFullLoginLinkFromLocalStorage, subscriberId, setSubscriberId, getCoBranding } =
        useContext(GlobalContext);
    const [verifyStaffAccountUiState, setVerifyStaffAccountUiState] = useState(EVerifyStaffAccountUiState.default);
    const [userId, setUserId] = useState('');
    const [code, setCode] = useState('');
    const [customerAccountName, setCustomerAccountName] = useState('');
    const { search: queryParams } = useLocation();

    // ---------
    // Setup
    // ---------
    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);
        const userId = params.get(EQueryStringParam.userId);
        const code = params.get(EQueryStringParam.code);
        const customerAccountName = params.get(EQueryStringParam.customerAccountName);
        const subscriberId = params.get(EQueryStringParam.subscriberId);
        if (userId && code) {
            setUserId(userId);
            setCode(code);
            customerAccountName && setCustomerAccountName(customerAccountName);
            subscriberId && setSubscriberId(subscriberId);
        } else {
            setVerifyStaffAccountUiState(EVerifyStaffAccountUiState.fatalError);
            console.error(
                `
                Failed to parse query string: 
                ${EQueryStringParam.userId} = ${userId ? userId : 'null'}
                ${EQueryStringParam.code} = ${code ? code : 'null'}
                `,
            );
        }
    };

    const initialize = () => {
        setStaffAccountType(EStaffAccountType.account);
        getFullLoginLinkFromLocalStorage();
        parseQueryString();
        setVerifyStaffAccountUiState(EVerifyStaffAccountUiState.initialzed);
    };

    const verifyStaffAccount = async () => {
        const requestParams = `${EQueryStringParam.userId}=${userId}&${EQueryStringParam.code}=${code}${
            subscriberId ? `&${EQueryStringParam.subscriberId}=${subscriberId}` : ''
        }`;
        try {
            await API.get.verifyStaffAccount(`?${requestParams}`);
        } catch (error) {
            console.error('Failed to verify staff account');
            setVerifyStaffAccountUiState(EVerifyStaffAccountUiState.fatalError);
        }
    };

    const getInitialData = async () => {
        if (subscriberId) {
            const [_, verificationResponse] = await Promise.allSettled([getCoBranding(), verifyStaffAccount()]);
            setVerifyStaffAccountUiState(EVerifyStaffAccountUiState.ready);
            if (verificationResponse.status === EPromiseResolution.fulfilled) {
                setVerifyStaffAccountUiState(EVerifyStaffAccountUiState.ready);
            }
        } else {
            await verifyStaffAccount();
            setVerifyStaffAccountUiState(EVerifyStaffAccountUiState.ready);
        }
    };

    useEffect(() => {
        switch (verifyStaffAccountUiState) {
            case EVerifyStaffAccountUiState.default:
                initialize();
                break;
            case EVerifyStaffAccountUiState.initialzed:
                getInitialData();
                break;
            default:
                break;
        }
    }, [verifyStaffAccountUiState]);

    return (
        <VerifyStaffAccountContext.Provider
            value={{
                verifyStaffAccountUiState,
                customerAccountName,
            }}
        >
            {children}
        </VerifyStaffAccountContext.Provider>
    );
};

export default VerifyStaffAccountContext;
